import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import {  collection, where, doc, getDoc, onSnapshot, orderBy, query, limit, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Timestamp } from "firebase/firestore"; // Import Timestamp
import { useNavigate } from 'react-router-dom';

export const GlobalStateContext = createContext();

export const useGlobalContext = () => useContext(GlobalStateContext);

export const GlobalStateProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [feedlots, setFeedlots] = useState([]);
  const [selectedFeedlot, setSelectedFeedlot] = useState(null);
  
  const [currentData, setCurrentData] = useState(null);
  const [currentData2, setCurrentData2] = useState(null);
  const [currentData3, setCurrentData3] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [realtimeData2, setRealtimeData2] = useState(null);
  const [unsubscribeRealtimeListener, setUnsubscribeRealtimeListener] = useState(null);
  const [unsubscribeRealtimeListenerST, setUnsubscribeRealtimeListenerST] = useState(null);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [noDataAlertRealTime, setNoDataAlertRealTime] = useState(false);
  const [noDataAlertRealTimeST, setNoDataAlertRealTimeST] = useState(false);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            
            const { Feedlots, ...otherUserData } = userData;
            setUserData(otherUserData);

            if (Feedlots && Array.isArray(Feedlots)) {
              const feedlotPromises = Feedlots.map(ref => getDoc(ref));
              const feedlotDocs = await Promise.all(feedlotPromises);

              const feedlotDataArray = feedlotDocs
                .map(feedlotDoc => feedlotDoc.exists() ? feedlotDoc.data() : null)
                .filter(data => data !== null);

              setFeedlots(feedlotDataArray);
            } else {
              setError('Feedlots field is missing or not an array');
            }
          } else {
            setError('User document not found');
          }
        } catch (err) {
          setError(err.message);
        }
      } else {
        setError('No authenticated user');
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate, auth]);

  const convertTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.seconds) {
      return null;
    }
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString();
  };

  const fetchCurrentData = async (sarCompID) => {
    try {
      const additionalDataRef = doc(db, sarCompID, 'CurrentData');
      const additionalDataSnap = await getDoc(additionalDataRef);
      if (additionalDataSnap.exists()) {
        return additionalDataSnap.data();
      }
      return null;
    } catch (err) {
      throw new Error(`Failed to fetch current data for ${sarCompID}: ${err.message}`);
    }
  };

  const fetchCurrentDataTMRO = async (tMRID) => {
    try {
      const additionalTmroRef = doc(db, tMRID, 'CurrentData');
      const additionalTmroSnap = await getDoc(additionalTmroRef);
      if (additionalTmroSnap.exists()) {
        return additionalTmroSnap.data();
      }
      return null;
    } catch (err) {
      throw new Error(`Failed to fetch current data for ${tMRID}: ${err.message}`);
    }
  };

  const fetchCurrentDataSarTime = async (SarTimeID) => {
    console.log("Get Current SarTime Data")
    try {
      const additionalSarTimeRef = doc(db, SarTimeID, 'CurrentData');
      const additionalSarTimeSnap = await getDoc(additionalSarTimeRef);
      if (additionalSarTimeSnap.exists()) {
        return additionalSarTimeSnap.data();
      }
      return null;
    } catch (err) {
      throw new Error(`Failed to fetch current data for ${SarTimeID}: ${err.message}`);
    }
  }

  const handleFeedlotSelect = async (event) => {
    const feedlotIndex = event.target.value;
    const feedlot = feedlots[feedlotIndex];

    console.log("feedlot: ", feedlot)
    setSelectedFeedlot(feedlot);

    if (unsubscribeRealtimeListener) {
      console.log('Unsubscribing from previous realtime listener for SarComputer');
      unsubscribeRealtimeListener();
      setUnsubscribeRealtimeListener(null);
    }

    if (unsubscribeRealtimeListenerST){
      console.log('Unsubscribing from previous realtime listener for SarTime');
      unsubscribeRealtimeListenerST();
      setUnsubscribeRealtimeListenerST(null);
    }

    setCurrentData(null);
    setCurrentData2(null);
    setCurrentData3(null)
    setRealtimeData(null);
    setRealtimeData2(null);

    if (feedlot && feedlot.SarCompID) {
      try {
        const data = await fetchCurrentData(feedlot.SarCompID);
        setCurrentData(data);
        setupRealtimeListener(feedlot.SarCompID);
      } catch (err) {
        setError(err.message);
      }
    }

    if (feedlot && feedlot.TMRID) {
      try {
        const data2 = await fetchCurrentDataTMRO(feedlot.TMRID);
        setCurrentData2(data2);
      } catch (err) {
        setError(err.message);
      }
    }
    if (feedlot && feedlot.SarTimeID) {
      try {
        const data = await fetchCurrentDataSarTime(feedlot.SarTimeID);
        setCurrentData3(data);
        setupRealtimeListenerST(feedlot.SarTimeID);
      } catch (err) {
        setError(err.message);
      }
    }

    if (feedlot && feedlot.PelletID) {
      
    }
  };

  const setupRealtimeListener = (sarCompID) => {
    setIsLoading(true); // Start loading
    const dataLoggingRef = collection(db, sarCompID, 'Archives', 'DataLogging');
    const dataLoggingQuery = query(dataLoggingRef, orderBy('timeStamp', 'desc'), limit(10));

    const unsubscribe = onSnapshot(dataLoggingQuery, (snapshot) => {
      setTimeout(() => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setRealtimeData(doc.data());
          setNoDataAlertRealTime(false);
        } else {
          setRealtimeData(null);
          setNoDataAlertRealTime(true)
        }
        setIsLoading(false); // Stop loading after delay
      }, 1000); // 1-second delay
    });

    setUnsubscribeRealtimeListener(() => unsubscribe);
  };

  const setupRealtimeListenerST = (SarTimeID) => {
    console.log("Getting SarTime Data")
    console.log(SarTimeID)
    setIsLoading(true); // Start loading
    console.log("Before Ref")
    const dataLoggingRef = collection(db, SarTimeID);
    //const dataLoggingRef = collection(db, SarTimeID, 'CurrentData');
    console.log("Ref done")

    const unsubscribe = onSnapshot(dataLoggingRef, (snapshot) => {
      setTimeout(() => {
        var doc
        var check = false
        if (!snapshot.empty) {
          snapshot.forEach((document) => {
            if(document.id === "RealTimeData"){
              doc = document;
              check = true
              return
            }
          });
          if(!check){
            doc = snapshot.docs[1]
          }
          console.log("In data get")
          //const doc = snapshot.docs[1];//RealTimeData should be the doc.
          //const doc = docTemp
          console.log(snapshot.docs)
          console.log(doc.data())
          setRealtimeData2(doc.data());
          setNoDataAlertRealTimeST(false);
        } else {
          setRealtimeData2(null);
          setNoDataAlertRealTimeST(true)
        }
        setIsLoading(false); // Stop loading after delay
      }, 1000); // 1-second delay
    });

    setUnsubscribeRealtimeListenerST(() => unsubscribe);
  };

  const fetchPelletTechData = async (id,system, field, fromTimestamp, toTimestamp, daysArr) => {

    
    try {

      
    
      
      let ref = collection(db, id, system.id, "DailyData")

      const startDate = new Date(fromTimestamp);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(toTimestamp);
      endDate.setHours(23, 59, 59, 999);

      const q = query(
        ref,
        where("timeStamp", ">=", Timestamp.fromMillis(startDate.getTime())),
        where("timeStamp", "<=", Timestamp.fromMillis(endDate.getTime())),
        orderBy("timeStamp", "desc") // Keep ordering
      ); 

      const querySnapshot = await getDocs(q);

      
      const values = [];


      if (querySnapshot.size === 0) {
        for (let i = 0; i < daysArr.length; i++) {
          values[i] = 0
        }

        return {values}
      }

      for (let i = 0; i < daysArr.length; i++) {

          const year = daysArr[i].split("-")[0] 
          const month = daysArr[i].split("-")[1]
          const day = daysArr[i].split("-")[2]


          for (let j = 0; j < querySnapshot.size; j++) {
            const data = querySnapshot.docs[j].data();

            const timestamp = data.timeStamp
            const date = timestamp.toDate();
            const dayTs = date.getDate(); 
            const monthTs = date.getMonth() + 1; 
            const yearTs = date.getFullYear()
           

            
            if (parseInt(day) === parseInt(dayTs) && parseInt(month) === parseInt(monthTs) && parseInt(year) === parseInt(yearTs)) {
              if (data[field]) { //protect against undefined


                if (field === "LSCTankLevel" || field === "garlicTankLevel") {
                  values.push(Math.round(data[field])); 
                } else {
                  values.push(data[field]); 

                }
                break
              }
              
            } 

            if (j == querySnapshot.size - 1) {
              values.push(0)
            }
          }
          
      }

       
      return {values }
    } catch (error) {
      console.error('Error fetching weekly averages data:', error);
      return { timestamps: [], values: [] };
    }



  }

  const fetchWeeklyAveragesData = async (sarCompID, field, fromTimestamp, toTimestamp, daysArr) => {
    try {
      const weeklyDataRef = collection(db, sarCompID, 'Archives', 'DailyAverages');

      const startDate = new Date(fromTimestamp);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(toTimestamp);
      endDate.setHours(23, 59, 59, 999);

      const weeklyDataQuery = query(
        weeklyDataRef,
        where("timeStamp", ">=", Timestamp.fromMillis(startDate.getTime())),
        where("timeStamp", "<=", Timestamp.fromMillis(endDate.getTime())),
        orderBy("timeStamp", "desc") // Keep ordering
      ); 

      const querySnapshot = await getDocs(weeklyDataQuery);

      
      const values = [];


      if (querySnapshot.size === 0) {
        for (let i = 0; i < daysArr.length; i++) {
          values[i] = 0
        }

        return {values}
      }

      for (let i = 0; i < daysArr.length; i++) {

          const year = daysArr[i].split("-")[0] 
          const month = daysArr[i].split("-")[1]
          const day = daysArr[i].split("-")[2]


          for (let j = 0; j < querySnapshot.size; j++) {
            const data = querySnapshot.docs[j].data();

            const timestamp = data.timeStamp
            const date = timestamp.toDate();
            const dayTs = date.getDate(); 
            const monthTs = date.getMonth() + 1; 
            const yearTs = date.getFullYear()
           

            
            if (parseInt(day) === parseInt(dayTs) && parseInt(month) === parseInt(monthTs) && parseInt(year) === parseInt(yearTs)) {
              if (data[field]) { //protect against undefined
                values.push(data[field]); 
                break
              }
              
            } 

            if (j == querySnapshot.size - 1) {
              values.push(0)
            }
          }
          
      }

       
      return {values }
    } catch (error) {
      console.error('Error fetching weekly averages data:', error);
      return { timestamps: [], values: [] };
    }
  };


  const fetchSevenDayData = async (sarCompID, field, fromTimestamp, toTimestamp, daysArr) => {
    try {
      const sevenDayDataRef = collection(db, sarCompID, 'Archives', 'SevenDay_Data');

      const fromDateStr = formatDateInt(fromTimestamp);
      const toDateStr = formatDateInt(toTimestamp);

      const startDate = new Date(fromTimestamp);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(toTimestamp);
      endDate.setHours(23, 59, 59, 999);

      const sevenDayDataQuery = query(
        sevenDayDataRef,
        where("DateStr", ">=", fromDateStr),
        where("DateStr", "<=", toDateStr),
        orderBy("DateStr", "asc") // Keep ordering
      );
      const querySnapshot = await getDocs(sevenDayDataQuery);
  
      const values = [];

      if (querySnapshot.size === 0) {
        for (let i = 0; i < daysArr.length; i++) {
          values[i] = 0
        }
        return {values}
      }


      for (let i = 0; i < daysArr.length; i++) {

          const year = daysArr[i].split("-")[0].substring(2,3) 
          const month = daysArr[i].split("-")[1]
          const day = daysArr[i].split("-")[2]

          for (let j = 0; j < querySnapshot.size; j++) {
            const data = querySnapshot.docs[j].data();

            const dateStr = data.Date
            const fbMonth = dateStr.split("/")[0]
            const fbDay = dateStr.split("/")[1]
            let fbYear = ""

            //onyl some entries have a timestamp in firebase
            if (data.timeStamp) {
              fbYear = data.timeStamp.toDate().getFullYear()
            } else {
              fbYear = data.DateStr.toString().substring(0,1)
            }
             
            
            if (parseInt(day) === parseInt(fbDay) && parseInt(month) === parseInt(fbMonth) && parseInt(year) === parseInt(fbYear.substring(0,1))) {
              
              if (data[field]) {
                values.push(data[field]);
                break
              }
              
            } 

            if (j == querySnapshot.size - 1) {
              values.push(0)
            }
          }
          
      }
      return {values }
    } catch (error) {
      console.error('Error fetching seven-day data:', error);
      return { dates: [], values: [] };
    }
  };

  const fetchSarTimeArchivesData = async (sarTimeID, field, fromTimestamp, toTimestamp, daysArr) => {

    const values = [];


    try {


      const sartimeArchivesRef = collection(db, sarTimeID, 'RealTimeData', 'dailyAverages');
    

      const startDate = new Date(fromTimestamp);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(toTimestamp);
      endDate.setHours(23, 59, 59, 999);

      const weeklyDataQuery = query(
        sartimeArchivesRef,
        where("timeStamp", ">=", Timestamp.fromMillis(startDate.getTime())),
        where("timeStamp", "<=", Timestamp.fromMillis(endDate.getTime())),
        orderBy("timeStamp", "desc") // Keep ordering
      ); 

      const querySnapshot = await getDocs(weeklyDataQuery);


      
      if (querySnapshot.size === 0) {
          for (let i = 0; i < daysArr.length; i++) {
            values[i] = 0
          }      
          return {values}
      }

      for (let i = 0; i < daysArr.length; i++) {

          const year = daysArr[i].split("-")[0] 
          const month = daysArr[i].split("-")[1]
          const day = daysArr[i].split("-")[2]



          for (let j = 0; j < querySnapshot.size; j++) {
            const data = querySnapshot.docs[j].data();

            const timestamp = data.timeStamp
            const date = timestamp.toDate();
            const dayTs = date.getDate(); 
            const monthTs = date.getMonth() + 1; 
            const yearTs = date.getFullYear()

            
            if (parseInt(day) === parseInt(dayTs) && parseInt(month) === parseInt(monthTs) && parseInt(year) === parseInt(yearTs)) {

              if (data[field.value]) { //protect against undefined
                values.push(data[field.value]); 
                break
              }
              
            } 

            if (j == querySnapshot.size - 1) {
              values.push(0)
            }
          }
          
      }

      return {values}
      
    } catch (error) {
      console.error('Error fetching sartime archives data:', error);
      return { dates: [], values: [] };
    }


    
  }

  const fetchPriorSevenDayDataTmro = async (tMRID, date) => {

    try {
      //need ref to both the unit1 and the volume
      const volumeRef = collection(db, tMRID, 'Archives', 'Volume');

      const days = []
      const dates = []
      

      //get last 7 dates
      for (let i = 0; i < 7; i++) {
        let formattedDate = `${date.getFullYear().toString().slice(2)}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
        let numericDate = Number(formattedDate);

        days.unshift({"TankLevel": null, "Batches": null, "GrainTotal": null, "Date": numericDate})
        dates.unshift(numericDate)
        
        date.setDate(date.getDate() - 1)
      }
      

      //Get all the data for each date
      const q = query(volumeRef, where('OperDate', 'in', dates))
        
      
      let vol_doc_ref = await getDocs(q)
      let vol_doc_arr = vol_doc_ref.docs

      vol_doc_arr.map((doc, ind) => {
          days[ind].TankLevel = doc.data().LSCVolume
          days[ind].Batches = doc.data().Batches
          days[ind].GrainTotal = doc.data().TotalTMR
      })

      //return an aray of objects representing each day
      return days

    } catch (error) {
      console.log(error.message)
      throw Error(error.message)
    }
}
  
  // Function to convert timestamp to "DDMMYY" format
const formatDateInt = (timestamp) => {
  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = String(date.getFullYear()).slice(-2); // Get last two digits of year
  return parseInt(`${year}${month}${day}`);
};

const formatDateFromStr = (dateNum) => {
  // Convert number to string and ensure it is 6 characters long
  const dateStr = String(dateNum).padStart(6, '0');

  // Extract day, month, year from `DDMMYY`
  const day = dateStr.slice(2, 4);
  const month = dateStr.slice(4, 6);
  const year = "20" + dateStr.slice(0, 2); // Assuming it's in 2000s; adjust if needed

  // Return formatted date string
  return `${day}/${month}/${year}`; // Format: YYYY-MM-DD
};


  const logReadOperation = async (tMRID, operationType, additionalInfo = {}) => {
    try {
      const logRef = collection(db, 'ReadLogs');
      await addDoc(logRef, {
        tMRID,
        operationType,
        timestamp: serverTimestamp(),
        ...additionalInfo,
      });
      console.log(`Logged ${operationType} operation for tMRID: ${tMRID}`);
    } catch (error) {
      console.error('Error logging read operation:', error);
    }
  };

  const fetchFieldsForDate = async (tMRID, date) => {
    console.log(`Fetching fields for date ${date} in feedlot ${tMRID}`);
    try {
      const unitRef = collection(db, tMRID, 'Archives', 'Unit1');
      
      // Ensure the date is treated as a number
      const numericDate = Number(date);
      
      // Query with where and orderBy
      const q = query(unitRef, where('OperDate', '==', numericDate), orderBy('OperDate', 'desc'), limit(100)); // Adjust the limit as necessary
      const querySnapshot = await getDocs(q);
  
      const fieldsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const filteredData = {};
  
        // Include only the required fields
        const requiredFields = ['Batch', 'Grain', 'LSCAdded', 'LSCVolume', 'WaterAdded'];
  
        requiredFields.forEach((field) => {
          if (data.hasOwnProperty(field)) {
            filteredData[field] = data[field];
          }
        });
  
        fieldsData.push({ id: doc.id, ...filteredData });
      });
      
      return fieldsData;
    } catch (error) {
      console.error(`Failed to fetch fields for date ${error.message}`);
      throw new Error(`Failed to fetch fields for date ${error.message}`);
    }
  };
  
  const fetchEndOfDayVolume = async (tMRID, date) => {
    try {
      const unitRef = collection(db, tMRID, 'Archives', 'Volume');
      
    
      // Ensure the date is treated as a number
      const numericDate = Number(date);

      const fieldsData = []

      const q = query(unitRef, where('OperDate', '==', numericDate));
      
      const snapshot = await getDocs(q)
      snapshot.forEach(doc => {
        fieldsData.push(doc.data())
      })

      if (fieldsData.length == 0) {
        return;
      } else return fieldsData[0]
    } catch (error) {
        console.log(error)
        throw new Error(`Failed to fetch volume for the date: ${error.message}`)
        
    }
}
  
  const fetchCombinedValuesForDate = (fieldsData, fieldsToSum) => {
    console.log('Calculating combined values from fields data');
  
    const combinedValues = fieldsToSum.reduce((acc, field) => {
      acc[field] = 0;
      return acc;
    }, {});
  
    fieldsData.forEach((data) => {
      fieldsToSum.forEach((field) => {
        if (data.hasOwnProperty(field)) {
          combinedValues[field] += Number(data[field]);
        }
      });
    });
  
    console.log(`Combined values: ${JSON.stringify(combinedValues)}`);
    return combinedValues;
  };
  
  
  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigate('/login');
    } catch (err) {
      console.error('Failed to logout:', err);
      setError('Failed to logout');
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <GlobalStateContext.Provider
      value={{
        userData,
        feedlots,
        selectedFeedlot,
        currentData,
        currentData2,
        currentData3,
        realtimeData,
        realtimeData2,
        error,
        isSidebarOpen,
        isSidebarCollapsed,
        isLoading,
        noDataAlertRealTime,
        noDataAlertRealTimeST,
        fetchPelletTechData,
        handleFeedlotSelect,
        convertTimestamp,
        fetchCurrentData,
        fetchCurrentDataTMRO,
        fetchEndOfDayVolume,
        setupRealtimeListener,
        setupRealtimeListenerST,
        fetchWeeklyAveragesData,
        fetchSevenDayData,
        fetchPriorSevenDayDataTmro,
        fetchFieldsForDate,
        fetchCombinedValuesForDate,
        fetchSarTimeArchivesData,
        handleLogout,
        toggleSidebar,
        toggleCollapse,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};
